
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModalA11y } from '../../hooks/useModalA11y';
import './ImageModal.css';

interface ImageModalProps {
  images: string[];
  initialIndex: number;
  onClose: () => void;
}

const SWIPE_DISMISS_THRESHOLD = 110;   // px down to trigger close
const SWIPE_NAV_THRESHOLD = 60;         // px horizontal to trigger image change
const TAP_MAX_MS = 250;                 // touch under this duration = tap, not drag

const ImageModal: React.FC<ImageModalProps> = ({ images, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Drag tracking: dy is vertical offset (swipe-to-dismiss), dx is
  // horizontal (swipe-to-navigate). We only commit to one axis once
  // the touch has clearly chosen a direction (>10px in one axis).
  const [dragDy, setDragDy] = useState(0);
  const [dragDx, setDragDx] = useState(0);
  const [chromeVisible, setChromeVisible] = useState(true);
  const touchStartRef = useRef<{ x: number; y: number; t: number; axis: 'none' | 'y' | 'x' } | null>(null);

  const handleDownload = async () => {
    if (downloading) return;
    const url = images[currentIndex];
    if (!url) return;
    setDownloading(true);
    try {
      const res = await fetch(url, { credentials: 'omit' });
      const blob = await res.blob();
      const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `onlyjalen-${Date.now()}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(url, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  // Body scroll lock, Escape, focus trap, focus restore — all from the hook.
  const dialogRef = useModalA11y({ onEscape: onClose });

  // Arrow navigation is gallery-specific, so it stays here.
  useEffect(() => {
    const handleArrow = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setCurrentIndex(i => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setCurrentIndex(i => Math.min(images.length - 1, i + 1));
    };
    window.addEventListener('keydown', handleArrow);
    return () => window.removeEventListener('keydown', handleArrow);
  }, [images.length]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Touch handlers: native phone gestures for a more natural feel.
  //  - Vertical swipe down → dismiss (fade backdrop + slide image)
  //  - Horizontal swipe   → previous/next image (when gallery has >1)
  //  - Quick tap on image → toggle chrome visibility (cleaner viewing)
  // We pick the axis after the first 10px of movement to avoid
  // wobble between modes, and we snap-back if the user releases
  // before crossing the threshold.
  const handleTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY, t: Date.now(), axis: 'none' };
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    const start = touchStartRef.current;
    if (!start || isZoomed) return; // zoomed = let native pan handle
    const t = e.touches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (start.axis === 'none') {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      start.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
    }
    if (start.axis === 'y') {
      // Only track downward drag; upward swipe shouldn't start the dismiss.
      setDragDy(dy > 0 ? dy : 0);
      setDragDx(0);
    } else {
      // Horizontal: dampen at the gallery edges so it feels rubber-bandy.
      const atLeftEdge = currentIndex === 0 && dx > 0;
      const atRightEdge = currentIndex === images.length - 1 && dx < 0;
      const eased = (atLeftEdge || atRightEdge) ? dx * 0.35 : dx;
      setDragDx(eased);
      setDragDy(0);
    }
  };
  const handleTouchEnd = () => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const durationMs = Date.now() - start.t;
    const wasTap = start.axis === 'none' && durationMs < TAP_MAX_MS;
    // Reset drag offsets — useEffect on currentIndex handles new image
    // settle; snap-back handled by CSS transitions.
    if (start.axis === 'y' && dragDy >= SWIPE_DISMISS_THRESHOLD) {
      setDragDy(0);
      onClose();
      return;
    }
    if (start.axis === 'x' && Math.abs(dragDx) >= SWIPE_NAV_THRESHOLD && images.length > 1) {
      if (dragDx < 0 && currentIndex < images.length - 1) {
        setCurrentIndex(i => i + 1);
      } else if (dragDx > 0 && currentIndex > 0) {
        setCurrentIndex(i => i - 1);
      }
    }
    setDragDx(0);
    setDragDy(0);
    if (wasTap) setChromeVisible(v => !v);
  };

  // Backdrop opacity fades as the user drags down — matches Insta's
  // gesture-driven dismiss feel.
  const dragProgress = Math.min(1, dragDy / SWIPE_DISMISS_THRESHOLD);
  const backdropOpacity = 1 - dragProgress * 0.6;
  const imageTransform = dragDy > 0
    ? `translateY(${dragDy}px) scale(${1 - dragProgress * 0.08})`
    : dragDx !== 0
      ? `translateX(${dragDx}px)`
      : undefined;

  // Portal so the overlay isn't trapped inside transformed ancestors
  // (`.post-card { transform: translateZ(0) }` would break position:fixed).
  return createPortal((
    <div
      className={`image-modal-backdrop${chromeVisible ? '' : ' chrome-hidden'}${dragDy > 0 || dragDx !== 0 ? ' dragging' : ''}`}
      onClick={handleBackdropClick}
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      tabIndex={-1}
      style={{ backgroundColor: `rgba(8, 4, 18, ${backdropOpacity * 0.95})` }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="image-modal-toolbar">
        <button
          className="image-modal-action"
          onClick={handleDownload}
          disabled={downloading}
          aria-label="Download image"
          title="Download to your device"
        >
          {downloading ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="im-spin">
              <path d="M21 12a9 9 0 11-6.219-8.56"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
              <polyline points="7,10 12,15 17,10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
          )}
        </button>
        <button className="image-modal-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div
        className={`image-modal-content ${isZoomed ? 'zoomed' : ''}`}
        style={imageTransform ? { transform: imageTransform } : undefined}
      >
        <img
          src={images[currentIndex]}
          alt=""
          onClick={() => setIsZoomed(!isZoomed)}
          draggable={false}
          onError={e => {
            const img = e.target as HTMLImageElement;
            img.style.background = '#1a0a2e';
            img.style.padding = '40px';
            img.alt = 'Image could not be loaded';
          }}
        />
      </div>

      {images.length > 1 && (
        <>
          <button
            className="image-modal-nav prev"
            onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
            disabled={currentIndex === 0}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            className="image-modal-nav next"
            onClick={() => setCurrentIndex(i => Math.min(images.length - 1, i + 1))}
            disabled={currentIndex === images.length - 1}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          <div className="image-modal-indicators">
            {images.map((_, i) => (
              <button
                key={i}
                className={`indicator ${i === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(i)}
              />
            ))}
          </div>
        </>
      )}

      <div className="image-modal-counter">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  ), document.body);
};

export default ImageModal;
